import React from 'react';
import { Field, FieldRenderProps, useForm, FieldProps } from 'react-final-form';
import AutoSave from '../AutoSave';
import NumberInput from '../../../components/Inputs/Number';
import { IndicatorStateOptions } from '../../../components/Indicator';
import { FieldState } from 'final-form';
// FIXME making custom props optional until this is resolved:
// https://github.com/final-form/react-final-form/issues/175
type NumberInputMetaProps = {
  invalid?: boolean;
  error?: string;
  initial?: any;
  disabled?: boolean;
  submitting?: boolean;
  submitFailed?: boolean;
  submitSucceeded?: boolean;
  dirtySinceLastSubmit?: boolean;
  dirty?: boolean;
};

const getIndicatorState = (
  props: NumberInputMetaProps,
): IndicatorStateOptions => {
  let state: IndicatorStateOptions = 'new';
  if (props.initial) state = 'complete';
  if (props.error || props.invalid) state = 'error';
  if (props.submitting && props.dirtySinceLastSubmit !== props.dirty)
    state = 'loading';
  return state;
};

interface NumberInputFieldComponentProps
  extends FieldRenderProps<number, HTMLInputElement> {
  text?: string;
  informationText?: string[];
  enabled?: boolean;
  supportingLink?: React.MouseEventHandler;
  handleSubmit?: () => void;
}
const NumberInputFieldComponent = ({
  input,
  meta,
  handleSubmit,
  enabled,
  ...rest
}: NumberInputFieldComponentProps) => {
  return (
    <AutoSave
      name={input.name}
      handleSubmit={handleSubmit ? handleSubmit : () => {}}>
      <NumberInput
        {...input}
        {...meta}
        enabled={enabled}
        // disabled={meta.submitting}
        indicatorState={getIndicatorState(meta)}
        {...rest}
      />
    </AutoSave>
  );
};

type minMaxValidatorFunctionType = (
  value: number,
  allValues: object,
  meta: FieldState<number> | undefined,
  min?: number,
  max?: number,
) => string | undefined;

const minMaxValidator: minMaxValidatorFunctionType = (
  value: number,
  _allValues: object,
  _meta: FieldState<number> | undefined,
  min?: number,
  max?: number,
) => {
  if (min && value < min) return `Minimum value is ${min}`;
  if (max && value > max) return `Maximum value is ${max}`;
  return undefined;
};

export interface NumberInputFieldProps
  extends FieldProps<number, FieldRenderProps<number>> {
  text?: string;
  informationText?: string[];
  supportingLink?: React.MouseEventHandler;
  enabled?: boolean;
  min?: number;
  max?: number;
}

const NumberField = (props: NumberInputFieldProps) => {
  const form = useForm();
  const required = !(props.enabled === false) && props.required;
  return (
    <Field
      component={NumberInputFieldComponent}
      handleSubmit={form.submit}
      {...props}
      required={required}
      validate={(value, allValues, meta) => {
        let error =
          required && props.validate
            ? props.validate(value, allValues, meta)
            : undefined;

        return (
          error || minMaxValidator(value, allValues, meta, props.min, props.max)
        );
      }}
    />
  );
};

export default NumberField;
